import type { GameContext } from "./types.ts";
import type { GameLoop } from "../engine/GameLoop.ts";
import { setupCombatHandlers } from "./handlers/combatHandlers.ts";
import { setupWaveHandlers } from "./handlers/waveHandlers.ts";
import { setupUIHandlers } from "./handlers/uiHandlers.ts";
import type {
  CombatHandlerDeps,
  HandlerState,
  UiHandlerDeps,
  WaveHandlerDeps,
} from "./handlers/types.ts";

/** Wire all EventBus subscriptions for the game. */
export function setupEventHandlers(
  ctx: GameContext,
  gameLoop: GameLoop,
): () => void {
  const { eventBus } = ctx;

  const state: HandlerState = {
    recursiveLoopProcessing: false,
    leaksThisWave: 0,
    runEndFired: false,
    coreBreakerShownThisWave: false,
  };

  const unsubs: (() => void)[] = [];

  // F3: closure-scoped run counters must reset when the player returns to menu
  unsubs.push(
    eventBus.on("game.returnToMenu", () => {
      state.leaksThisWave = 0;
      state.recursiveLoopProcessing = false;
      state.runEndFired = false;
      state.coreBreakerShownThisWave = false;
    }),
  );

  unsubs.push(
    eventBus.on("economy.earned", (d: unknown) => {
      const ev = d as { amount: number };
      ctx.gs.runStats.goldEarned += ev.amount;
    }),
  );

  unsubs.push(
    eventBus.on("economy.spent", (d: unknown) => {
      const ev = d as { amount: number };
      ctx.gs.runStats.goldSpent += ev.amount;
    }),
  );

  // Construct narrow combat handler deps. gs is the live RuntimeState so that
  // its mutations (loseLives/setLives via stateIntents, counters) propagate to
  // the owner; failsafe is a live accessor over the mutable failsafeAvailable flag
  // (written at run-start AFTER setup) so reads stay fresh and consume() writes
  // back to ctx rather than a stale copy.
  const combatDeps: CombatHandlerDeps = {
    eventBus: ctx.eventBus,
    gs: ctx.gs,
    particleSystem: ctx.particleSystem,
    screenEffects: ctx.screenEffects,
    draftSystem: ctx.draftSystem,
    projectilePool: ctx.projectilePool,
    floatingDmg: ctx.floatingDmg,
    breachSystem: ctx.breachSystem,
    waveManager: ctx.waveManager,
    gridRenderer: ctx.gridRenderer,
    scoreSystem: ctx.scoreSystem,
    gameOverScreen: ctx.gameOverScreen,
    ...(ctx.metaState ? { metaState: ctx.metaState } : {}),
    gameBanner: ctx.gameBanner,
    failsafe: {
      get available() {
        return ctx.failsafeAvailable;
      },
      consume() {
        ctx.failsafeAvailable = false;
      },
    },
    gameLoop,
    perfGovernor: ctx.perfGovernor,
    state,
  };
  unsubs.push(...setupCombatHandlers(combatDeps));

  // Construct narrow wave handler deps. gs is the live RuntimeState (handlers
  // and forwarded card effects reassign gs.lives/counters in place).
  const waveDeps: WaveHandlerDeps = {
    eventBus: ctx.eventBus,
    gs: ctx.gs,
    gridRenderer: ctx.gridRenderer,
    particleSystem: ctx.particleSystem,
    screenEffects: ctx.screenEffects,
    draftSystem: ctx.draftSystem,
    draftUI: ctx.draftUI,
    skillOverview: ctx.skillOverview,
    skillDeckBar: ctx.skillDeckBar,
    economy: ctx.economy,
    combatSystem: ctx.combatSystem,
    grid: ctx.grid,
    spawnAlert: ctx.spawnAlert,
    pathRevealAnimation: ctx.pathRevealAnimation,
    breachSystem: ctx.breachSystem,
    breachCinematic: ctx.breachCinematic,
    spawnDetectionSystem: ctx.spawnDetectionSystem,
    gameBanner: ctx.gameBanner,
    recalculatePath: ctx.recalculatePath,
    synergySystem: ctx.synergySystem,
    scoreSystem: ctx.scoreSystem,
    waveManager: ctx.waveManager,
    rng: ctx.rng,
    moduleBaselines: ctx.moduleBaselines,
    payloadRack: ctx.payloadRack,
    ...(ctx.commsRouter ? { commsRouter: ctx.commsRouter } : {}),
  };
  unsubs.push(...setupWaveHandlers(waveDeps, state));

  // Construct narrow UI handler deps
  const uiHandlerDeps: UiHandlerDeps = {
    eventBus: ctx.eventBus,
    gs: { towers: ctx.gs.towers, firewalls: ctx.gs.firewalls },
    particleSystem: ctx.particleSystem,
    screenEffects: ctx.screenEffects,
    economy: ctx.economy,
    grid: ctx.grid,
    recalculatePath: ctx.recalculatePath,
    synergySystem: ctx.synergySystem,
    gameBanner: ctx.gameBanner,
    gridRenderer: ctx.gridRenderer,
    floatingDmg: ctx.floatingDmg,
    waveManager: ctx.waveManager,
    pathRegistry: ctx.pathRegistry,
    scannerReadout: ctx.scannerReadout,
    ...(ctx.commsRouter ? { commsRouter: ctx.commsRouter } : {}),
  };
  unsubs.push(...setupUIHandlers(uiHandlerDeps));

  return () => {
    for (const unsub of unsubs) unsub();
  };
}
